import { setCampaignContext } from '@/utils/campaignContext'

const DEFAULT_LANGUAGE = 'US'
const DEFAULT_ACTIVITY_ID = import.meta.env.VITE_DG_CAMPAIGN_ID || ''

// 获取 Flutter 提供的 JSBridge 对象，若不存在则返回 null
const getBridge = () => {
  if (typeof window === 'undefined') {
    return null
  }
  const bridge = window.flutter_inappwebview
  if (bridge && typeof bridge.callHandler === 'function') {
    return bridge
  }
  return null
}

// 调用 JSBridge 指定方法，统一捕获异常并返回 null
const callBridge = async (method, ...args) => {
  const bridge = getBridge()
  if (!bridge) {
    console.warn(`[JSBridge] ${method} 调用失败：Bridge 未就绪`)
    return null
  }
  try {
    return await bridge.callHandler(method, ...args)
  } catch (error) {
    console.warn(`[JSBridge] ${method} 调用异常:`, error)
    return null
  }
}

// 将传入值转换为去除首尾空格的字符串
const sanitizeString = (value) =>
  typeof value === 'string'
    ? value.trim()
    : value === undefined || value === null
      ? ''
      : String(value).trim()

// 安全解析 JSON 字符串，失败时返回 null
const parseJson = (text) => {
  if (typeof text !== 'string' || !text.trim()) {
    return null
  }
  try {
    return JSON.parse(text)
  } catch (error) {
    console.warn('[JSBridge] JSON 解析失败:', error)
    return null
  }
}

// 从当前页面 URL 中解析 activityId 查询参数
const getActivityIdFromUrl = () => {
  if (typeof window === 'undefined' || !window.location) {
    return ''
  }
  try {
    const url = new URL(window.location.href)
    return sanitizeString(url.searchParams.get('activityId') || '')
  } catch (error) {
    console.warn('[JSBridge] 解析 activityId 失败:', error)
    return ''
  }
}

// 组合分享内容与链接，自动去除多余空格
const buildShareText = (content, link) => {
  const parts = [sanitizeString(content), sanitizeString(link)].filter(Boolean)
  return parts.join(' ').trim()
}

export const JSBridge = {
  isAvailable: () => Boolean(getBridge()),

  // 获取登录 token，并写入活动上下文
  async getToken() {
    const res = await callBridge('getToken')
    const token = sanitizeString(res?.token ?? res)
    if (token) {
      setCampaignContext({ token })
    }
    return token
  },

  // 获取语言偏好，默认返回 US
  async getLanguage() {
    const res = await callBridge('getLanguage')
    const language = sanitizeString(res?.language ?? res) || DEFAULT_LANGUAGE
    setCampaignContext({ language })
    return language
  },

  // 获取活动 ID（优先取 URL 参数）
  async getActivityId() {
    const activityId = getActivityIdFromUrl() || DEFAULT_ACTIVITY_ID
    if (activityId) {
      setCampaignContext({ activityId })
    }
    return activityId
  },

  // 获取完整设备信息（JSON）
  async getDeviceInfo() {
    const res = await callBridge('getDeviceInfo')
    if (!res) {
      return null
    }
    if (typeof res.deviceInfo === 'string') {
      return parseJson(res.deviceInfo) || res.deviceInfo
    }
    return res.deviceInfo || res
  },

  // 获取设备唯一 ID（优先 uniqueId）
  async getDeviceId() {
    const res = await callBridge('getDeviceId')
    const deviceId =
      sanitizeString(res?.uniqueId) ||
      sanitizeString(res?.deviceId) ||
      sanitizeString(res)
    if (deviceId) {
      setCampaignContext({ deviceId })
    }
    return deviceId
  },

  // 获取用户信息（仅包含 userId/userName/country/state/city）
  async getUserInfo() {
    const res = await callBridge('getUserInfo')
    if (!res) {
      return null
    }
    const raw =
      typeof res.userInfo === 'string'
        ? parseJson(res.userInfo)
        : res.userInfo || res
    if (!raw || typeof raw !== 'object') {
      return null
    }
    const userInfo = {
      userId: sanitizeString(raw.id || raw.userId || raw.uid),
      userName: sanitizeString(raw.userName || raw.name || raw.username || raw.nickName || raw.nickname),
      country: sanitizeString(raw.country),
      state: sanitizeString(raw.state),
      city: sanitizeString(raw.city)
    }
    Object.keys(userInfo).forEach((key) => {
      if (!userInfo[key]) {
        delete userInfo[key]
      }
    })
    return userInfo
  },

  // 触发分享能力，将内容与链接拼接
  async shareToX(content = '', link = '') {
    const payload = buildShareText(content, link)
    return callBridge('share', payload)
  },

  // 调用原生复制功能
  async copyText(text) {
    const sanitized = sanitizeString(text)
    if (!sanitized) {
      return null
    }
    return callBridge('copy', { text: sanitized })
  },

  // 指示 App 跳转到指定路由
  async navigateToApp(routeName, options = {}) {
    const sanitizedRoute = sanitizeString(routeName)
    if (!sanitizedRoute) {
      return null
    }
    return callBridge('navigateToApp', {
      routeName: sanitizedRoute,
      ...options
    })
  },
  
  // 链接跳转（支持三种跳转类型：1=App页面跳转 2=站内H5跳转 3=外链跳转）
  // 仅支持对象入参：JSBridge.linkJump({ jumpType: 2, jumpLink: 'https://example.com/page' })
  async linkJump(options = {}) {
    const jumpTypeRaw = options?.jumpType
    const jumpLinkRaw = options?.jumpLink

    let jumpType = Number(jumpTypeRaw)
    let jumpLink = sanitizeString(jumpLinkRaw || '')
    // 去除示例中的反引号与多余空格
    jumpLink = jumpLink.replace(/`/g, '').trim()

    if (![1, 2, 3].includes(jumpType)) {
      jumpType = 2
    }
    if (!jumpLink) {
      return { success: false, message: '跳转链接为空' }
    }

    // 优先尝试调用原生（对象入参）
    const resObj = await callBridge('linkJump', { jumpType, jumpLink })
    if (resObj != null) {
      if (typeof resObj === 'object' && 'success' in resObj) {
        return resObj
      }
      return { success: true }
    }

    // Bridge 不可用时的浏览器兜底
    try {
      if (jumpType === 2) {
        if (typeof window !== 'undefined' && window.location) {
          window.location.assign(jumpLink)
          return { success: true, message: 'Bridge 不可用，已使用站内跳转' }
        }
      } else if (jumpType === 3) {
        if (typeof window !== 'undefined') {
          const isHttp = /^https?:\/\//i.test(jumpLink)
          if (isHttp) {
            window.location.href = jumpLink
          } else {
            window.open(jumpLink, '_blank')
          }
          return { success: true, message: 'Bridge 不可用，已使用外链跳转' }
        }
      }
      return { success: false, message: 'Bridge 不可用，无法跳转到App页面' }
    } catch (error) {
      return { success: false, message: String(error?.message || error) }
    }
  },
  
  // 统一返回上一页方法
  // 入参：对象或布尔（true 等同 { closeWebView: true }）
  // 出参：{ success: boolean, message?: string }
  async navigateBack(arg = {}) {
    const shouldClose = arg === true || (arg && arg.closeWebView === true)

    if (shouldClose) {
      return JSBridge.closeWebView()
    }

    // 优先尝试原生处理（可能内部已判断 WebView 历史）
    const nativeRes = await callBridge('navigateBack', { closeWebView: false })
    if (nativeRes && typeof nativeRes === 'object' && 'success' in nativeRes) {
      return nativeRes
    }

    // 浏览器回退兜底：有历史则后退，否则尝试关闭
    if (typeof window !== 'undefined' && window.history && window.history.length > 1) {
      try {
        window.history.back()
        return { success: true }
      } catch (error) {
        return { success: false, message: String(error?.message || error) }
      }
    }

    return JSBridge.closeWebView()
  },
  // 设置app页面标题 setNavigationTitle
  async setNavigationTitle(title) {
    // if (!title) {
    //   return null
    // }
    return callBridge('setNavigationTitle', { title })
  },

  // 批量/单项设置导航栏颜色
  // 用法一：setNavigationColors({ bgColor, titleColor, iconColor })
  // 用法二：setNavigationColors({ type: 'title'|'background'|'icon', color })
  // 返回：{ success: boolean, message?: string }
  async setNavigationColors(options = {}) {
    const type = sanitizeString(options.type)
    const color = sanitizeString(options.color)

    const bgColor = sanitizeString(options.bgColor)
    const titleColor = sanitizeString(options.titleColor)
    const iconColor = sanitizeString(options.iconColor)

    let payload = {}
    if (type && color) {
      payload = { type, color }
    } else {
      if (!bgColor && !titleColor && !iconColor) {
        return { success: false, message: '未提供有效的颜色参数' }
      }
      if (bgColor) payload.bgColor = bgColor
      if (titleColor) payload.titleColor = titleColor
      if (iconColor) payload.iconColor = iconColor
    }

    const res = await callBridge('setNavigationColors', payload)
    if (res && typeof res === 'object' && 'success' in res) {
      return res
    }
    return { success: res != null }
  },

  // 仅设置标题颜色（便捷方法）
  // 返回：{ success: boolean, message?: string }
  async setTitleColorOnly(color) {
    const sanitized = sanitizeString(color)
    if (!sanitized) {
      return { success: false, message: '颜色值为空' }
    }
    const res = await callBridge('setNavigationColors', { type: 'title', color: sanitized })
    if (res && typeof res === 'object' && 'success' in res) {
      return res
    }
    return { success: res != null }
  },

  // 直接关闭当前 WebView，不尝试 WebView 内部历史
  // 出参：{ success: boolean, message?: string }
  async closeWebView() {
    let res = await callBridge('closeWebView')
    if (res && typeof res === 'object' && 'success' in res) {
      return res
    }else {
      res = await callBridge('navigateBack', { closeWebView: true })
    }

    // 当 Bridge 不可用时的浏览器兜底行为
    if (!getBridge()) {
      if (typeof window !== 'undefined') {
        try {
          // 在浏览器环境中无法真正“关闭”，退回上一页作为降级处理
          if (window.history && window.history.length > 1) {
            window.history.back()
            return { success: true, message: 'Bridge 不可用，已使用浏览器后退' }
          }
          // 尝试关闭窗口（仅在由脚本打开的窗口生效）
          window.close?.()
          return { success: false, message: 'Bridge 不可用，无法关闭当前页面' }
        } catch (error) {
          return { success: false, message: String(error?.message || error) }
        }
      }
      return { success: false, message: 'Bridge 不可用' }
    }

    // 原生返回无结构时，视为成功
    return { success: true }
  },

  // 选择并上传媒体文件（图片/拍照/视频），委托 APP 端完成选择和上传
  // 入参：{ type: 'image'|'photo'|'video', maxCount?: number, maxSizeBytes?: number }
  //   type='photo'  调相机拍照（单张）
  //   type='image'  从相册选图片（支持多张，maxCount 默认 5）
  //   type='video'  选择视频（单个），maxSizeBytes 用于让 APP 上传前兜底限制视频大小
  // 返回：{ success: boolean, data: [{ url, sourceUrl, compressUrl?, mimeType?, width?, height? }] }
  async pickMedia(options = {}) {
    const type = sanitizeString(options.type) || 'image'
    const maxCount = Number(options.maxCount) || 5
    const payload = { type, maxCount }
    const maxSizeBytes = Number(options.maxSizeBytes)
    if (Number.isFinite(maxSizeBytes) && maxSizeBytes > 0) {
      payload.maxSizeBytes = Math.floor(maxSizeBytes)
    }
    const res = await callBridge('pickMedia', payload)
    if (res && typeof res === 'object') {
      return res
    }
    return { success: false, data: [] }
  }
}



export const isJSBridgeAvailable = () => JSBridge.isAvailable()

export const __internals__ = {
  getBridge,
  sanitizeString,
  buildShareText,
  getActivityIdFromUrl
}
